import { useCallback, useEffect, useRef, type RefObject } from "react";

/** Follow growing answers only while the reader is at the latest content. */
export function useConversationScrollFollow(scrollRef: RefObject<HTMLDivElement | null>) {
  const following = useRef(false);
  const frame = useRef<number | null>(null);
  const suspended = useRef(false);
  const pauseFollowing = useCallback(() => {
    following.current = false;
    suspended.current = true;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);
  const followLatest = useCallback(() => {
    suspended.current = false;
    following.current = true;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const root = scrollRef.current;
      if (root) root.scrollTop = root.scrollHeight;
    });
  }, [scrollRef]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    let previousHeight = root.scrollHeight;
    const readPosition = () => {
      if (!suspended.current) following.current = root.scrollHeight - root.clientHeight - root.scrollTop <= 48;
    };
    const resumeReading = () => { suspended.current = false; readPosition(); };
    const resize = new ResizeObserver(() => {
      const height = root.scrollHeight;
      const wasNearBottom = previousHeight - root.clientHeight - root.scrollTop <= 48;
      const shouldFollow = height > previousHeight ? wasNearBottom : following.current;
      previousHeight = height;
      if (!suspended.current && shouldFollow) followLatest();
    });
    resize.observe(root);
    for (const child of root.children) resize.observe(child);
    const mutation = new MutationObserver(() => {
      for (const child of root.children) resize.observe(child);
    });
    mutation.observe(root, { childList: true });
    readPosition();
    root.addEventListener("scroll", readPosition, { passive: true });
    root.addEventListener("wheel", resumeReading, { passive: true });
    root.addEventListener("touchstart", resumeReading, { passive: true });
    root.addEventListener("keydown", resumeReading);
    return () => {
      resize.disconnect();
      mutation.disconnect();
      root.removeEventListener("scroll", readPosition);
      root.removeEventListener("wheel", resumeReading);
      root.removeEventListener("touchstart", resumeReading);
      root.removeEventListener("keydown", resumeReading);
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    };
  }, [followLatest, scrollRef]);
  return { followLatest, pauseFollowing };
}
